import {tokens} from '@commercial-helios/ui/theme'
import {graphql, useStaticQuery} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import type {AllBlogPostsQuery} from '@helios/blog/src/graphql-types'
import {BlogPostCard, nodeToPostProps} from '@helios/shared/components/blog-post-card'
import Button from '@helios/shared/components/button'
import type {Props as PrismicLinkProps} from '@helios/shared/components/prismic-link'
import PrismicLink from '@helios/shared/components/prismic-link'
import {Layout, Section} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import mq from '@helios/shared/utils/media-query'

interface Props {
    title: string
    ctaText: string
    ctaLink: PrismicLinkProps
    dataTestId?: string
}

export const LatestBlogPosts = ({title, ctaText, ctaLink, dataTestId}: Props) => {
    const {allPrismicBlogPost} = useStaticQuery<AllBlogPostsQuery>(query)
    const posts = allPrismicBlogPost.nodes.map(nodeToPostProps)

    return (
        <Section data-testid={dataTestId}>
            <Text variant="heading-large">{title}</Text>
            <StyledLayout variant="three-equal" data-testid="latest-blog-posts">
                {posts.map((blogPost) => (
                    <BlogPostCard {...blogPost} isExternal />
                ))}
            </StyledLayout>
            <PrismicLink {...ctaLink} data-testid="latest-blog-posts-cta">
                <Button variant="outline" as="span">
                    {ctaText}
                </Button>
            </PrismicLink>
        </Section>
    )
}

const StyledLayout = styled(Layout)`
    margin-top: ${tokens.spacing40};
    margin-bottom: ${tokens.spacing20};

    ${mq.min('sm')} {
        margin: ${tokens.spacing60} 0;
    }
`

const query = graphql`
    query LatestBlogPostsForPeople {
        allPrismicBlogPost(
            filter: {data: {category: {uid: {eq: "pleo-people"}}}, lang: {eq: "en-gb"}}
            limit: 3
            sort: {order: DESC, fields: data___publish_date}
        ) {
            nodes {
                lang
                uid
                data {
                    title
                    excerpt
                    publish_date
                    hero_image {
                        dimensions {
                            width
                            height
                        }
                        alt
                        copyright
                        url
                    }
                    category {
                        uid
                        document {
                            ... on PrismicBlogCategory {
                                _previewable
                                data {
                                    name
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`
